// `fetch`-based HTTP primitive (headers, CSRF, body, abort, response
// parsing) shared by every gateway.

import { getCsrfToken } from './getCsrfToken';
import { camelCaseKeys, snakeCaseKeys } from './casing';
import { ApiRequestFailure } from './error/apiRequestFailure';

export type JSONObject = { [key: string]: JSONValue };

export type JSONValue =
  | string
  | number
  | boolean
  | null
  | JSONObject
  | JSONValue[];

export type HttpMethod = 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE' | 'HEAD';

export type ErrorState =
  | 'NO_INTERNET_CONNECTION'
  | 'UNAUTHORIZED'
  | 'UNEXPECTED_ERROR';

// Query params are limited to flat keys and arrays of primitives (`key[]=`) —
// the shapes call sites actually use. Nested objects (`key[sub]=`) are
// deliberately unsupported; nothing needs them.
export type QueryParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean | null | undefined>;

export type ApiClientOptions = {
  url: string;
  method?: HttpMethod;
  // `FormData` for multipart bodies (e.g. file uploads) — passed through to
  // `fetch` as-is, letting the browser set its own multipart Content-Type
  // (with boundary) instead of the default JSON one.
  data?: JSONValue | FormData;
  // Appended to `url` as a `?query` string — independent of `data`/method,
  // so a GET's query params and a POST's JSON body can both be expressed
  // without callers hand-building URLs.
  params?: Record<string, QueryParamValue>;
  signal?: AbortSignal;
  // Declares the endpoint's wire casing as snake_case: `data` keys are
  // written in camelCase and converted on the way out, and response bodies
  // (including a failure's `responseJson`) convert back to camelCase.
  // Opt-in, not the default — not every endpoint speaks snake_case (the v3
  // APIs read camelCase params, e.g. `venueIds` in groups/packages), and
  // some snake_case payloads hold keys the converter would mangle
  // (digits: `utf8` → `utf_8`). `params` are sent as written.
  casing?: 'snake';
  // Forwarded to `fetch`'s `cache` mode. `fetch` defaults to the browser's
  // normal HTTP cache; pass `'no-store'` for a GET that must never be served a
  // cached response (e.g. venue search).
  cache?: RequestCache;
};

// Safe methods: no request body, and no CSRF token — Rails does not verify one
// for GET/HEAD.
const METHODS_WITHOUT_BODY: ReadonlySet<HttpMethod> = new Set(['GET', 'HEAD']);

// `apiClient` re-throws AbortError untouched, so rejection handlers need a
// way to tell an expected abort from a real `ApiRequestFailure`.
export function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name: unknown }).name === 'AbortError'
  );
}

function errorStateFromResponse(response: Response): ErrorState {
  if (response.status === 403) {
    return 'UNAUTHORIZED';
  }
  return 'UNEXPECTED_ERROR';
}

function toRequestBody(data: JSONValue | FormData | undefined): BodyInit {
  return data instanceof FormData ? data : JSON.stringify(data);
}

export async function apiClient<T extends JSONValue = JSONValue>(
  options: ApiClientOptions
): Promise<T> {
  const method = options.method ?? 'GET';
  const includesBody = !METHODS_WITHOUT_BODY.has(method);
  const isFormData = options.data instanceof FormData;
  const data =
    options.casing === 'snake' && options.data !== undefined && !isFormData
      ? snakeCaseKeys(options.data as JSONValue)
      : options.data;
  const queryString = options.params ? toQueryString(options.params) : '';
  // `&` when the url already carries a query string (e.g. an auth token
  // appended by `withAuthTokenIfExists`).
  const url = queryString
    ? `${options.url}${options.url.includes('?') ? '&' : '?'}${queryString}`
    : options.url;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Some server-side branches (e.g.
    // ApplicationController#modal_layout_if_xhr) key off `request.xhr?`.
    'X-Requested-With': 'XMLHttpRequest',
    ...(includesBody && !isFormData
      ? { 'Content-Type': 'application/json' }
      : {})
  };

  if (includesBody) {
    const token = getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
    signal: options.signal,
    ...(options.cache ? { cache: options.cache } : {}),
    ...(includesBody ? { body: toRequestBody(data) } : {})
  };

  let response: Response;
  let text: string;
  try {
    response = await fetch(url, init);
    // Read inside the same try — the connection can still drop while the
    // body is streaming, and that failure must map to ApiRequestFailure too.
    text = await response.text();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw new ApiRequestFailure({
      requestOptions: options,
      responseJson: null,
      responseText: '',
      errorState: 'NO_INTERNET_CONNECTION'
    });
  }
  let parsedBody: JSONValue | undefined;
  let parseFailed = false;
  if (text.length > 0) {
    try {
      parsedBody = JSON.parse(text);
    } catch {
      parseFailed = true;
    }
  }

  if (response.ok) {
    if (parseFailed) {
      // A 2xx whose body isn't valid JSON is a failure, not a success with
      // no data: a session-timeout login page served as 200 must not reach a
      // caller expecting a payload. See ADR 0016.
      throw new ApiRequestFailure({
        requestOptions: options,
        responseJson: null,
        responseText: text,
        errorState: 'UNEXPECTED_ERROR'
      });
    }

    // The parsed body can legitimately be `undefined` here (e.g. an empty
    // 204 response) even though the return type is `T`; callers for those
    // endpoints don't read the result. See ADR 0016.
    if (options.casing === 'snake' && parsedBody !== undefined) {
      return camelCaseKeys<T>(parsedBody);
    }
    return parsedBody as T;
  }

  throw new ApiRequestFailure({
    requestOptions: options,
    responseJson:
      options.casing === 'snake' && parsedBody !== undefined
        ? camelCaseKeys(parsedBody)
        : (parsedBody ?? null),
    responseText: text,
    errorState: errorStateFromResponse(response)
  });
}

function toQueryStringValue(
  value: string | number | boolean | null | undefined
): string {
  return value === null || value === undefined ? '' : String(value);
}

export function toQueryString(params: Record<string, QueryParamValue>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      // A key that already ends in `[]` (a Rails array-param name like a
      // `cuisine[]` checkbox group) is the array marker itself — append to
      // it verbatim rather than doubling it to `cuisine[][]`, which Rack
      // parses as a nested array.
      const arrayKey = key.endsWith('[]') ? key : `${key}[]`;
      for (const item of value) {
        searchParams.append(arrayKey, toQueryStringValue(item));
      }
    } else {
      searchParams.append(key, toQueryStringValue(value));
    }
  }
  return searchParams.toString();
}
